import React, { useState, useEffect } from 'react'

import { authorization } from '../../../Module/Auth/Auth'
import { CHAT_TYPE } from '../options'
import { chatParticipantsSearch } from '../../../Module/graphQL'
import {
  createConversationTalkJs,
  createUpdateTalkJsUser,
  getUnreadStatuses,
  getUserTalkJs,
  joinToConversation,
  sendMessageTalkJs,
} from '../api'
import ChatV2View from '../component/ChatV2View'
import {
  getAdminsByCountryId,
  getCityById,
  getCityManagesByCityId,
} from '../gql'
import {
  ROLE_ADMIN,
  ROLE_CITY_MANAGER,
  ROLE_DISTRIBUTOR,
  ROLE_SPY,
  TALK_JS_CHAT_TYPES_MAP,
} from '../../../shared/constants'
import { convertArrayToHashMap } from '../../../shared/libraries/dataSets'
import { findConversationAndJoin, getCityList } from '../utils'

const ChatV2 = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const conversationIdParams = urlParams.get('conversationId')

  const user = authorization.getUser()
  const [viewType, setViewType] = useState(
    conversationIdParams ? CHAT_TYPE.CLIENTS_CHAT : CHAT_TYPE.SINGLE_CHAT,
  )
  const [participants, setParticipants] = useState([])
  const [conversationId, setConversationId] = useState(conversationIdParams)
  const [isLoading, setIsLoading] = useState(false)
  const [citiesList, setCitiesList] = useState(null)
  const [chatOptions, setChatOptions] = useState([])
  const [cities, setCities] = useState([])
  const [unreadStatuses, setUnreadStatuses] = useState(null)

  useEffect(() => {
    if (conversationIdParams) {
      const cleanedUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, cleanedUrl)
    }
  }, [conversationIdParams])

  const getChatUnreadStatuses = async () => {
    const statuses = await getUnreadStatuses(user.id)
    setUnreadStatuses(statuses)
  }

  const getChatOptions = async () => {
    const options = [
      { tab: 'Chats', value: CHAT_TYPE.SINGLE_CHAT },
      {
        tab: 'Spy reports',
        value: CHAT_TYPE.REPORT_CHAT,
      },
      { tab: 'Channels', value: CHAT_TYPE.GROUP_CHAT },
    ]
    if (user.role !== ROLE_CITY_MANAGER) {
      options.push({ tab: 'Clients', value: CHAT_TYPE.CLIENTS_CHAT })
    }
    setChatOptions(options)
  }

  useEffect(() => {
    getChatOptions()
    getChatUnreadStatuses()
  }, [])

  const checkUserExistTalkJs = async id => {
    const { errorCode } = await getUserTalkJs(id)

    if (errorCode === 'USER_NOT_FOUND') {
      await createUpdateTalkJsUser(user)

      const cityResult = await getCityById(user.cityId)
      const countryId = cityResult.data.city.country.id
      await findConversationAndJoin({
        userId: user.id,
        role: user.role,
        cityId: user.cityId,
        countryId: countryId,
      })
    }
  }
  const getCitiesList = async () => {
    const { cityNames, cityList } = await getCityList(user)
    setCitiesList(cityNames)
    setCities(cityList)
  }
  useEffect(() => {
    getCitiesList()
    checkUserExistTalkJs(user.id)
  }, [])

  const onItemAdd = ({ item }) => {
    setParticipants([...participants, item])
  }

  const onItemDelete = id => {
    const updatedParticipants = participants.filter(
      participant => participant.id != id,
    )
    setParticipants(updatedParticipants)
  }

  const searchRequestResolver = async (
    searchedString,
    selectedCities,
    selectedRole,
  ) => {
    const authToken = await authorization.getAPIKey()
    const response = await chatParticipantsSearch(
      authToken,
      searchedString,
      selectedCities,
      selectedRole,
    )
    const result = await response.json()

    return [
      {
        title: 'Users',
        results: result.data.chatParticipantsSearch.users.map(resultItem => ({
          item: resultItem,
          label: 'PARTICIPANT',
        })),
      },
    ]
  }

  const searchRequestHandler = async ({
    searchString,
    selectedCities,
    selectedRole,
  }) => {
    const formattedCitiesList = selectedCities.filter(item => item !== 'All')
    const result = await searchRequestResolver(
      searchString,
      formattedCitiesList,
      selectedRole,
    )
    if (participants.length > 0) {
      const participantsIds = participants.map(({ id }) => id)
      return [
        {
          title: result[0].title,
          results: result[0].results.filter(data => {
            return !participantsIds.includes(data.item.id)
          }),
        },
      ]
    }
    return result
  }

  const handleCreateConversation = async participants => {
    if (!participants || participants.length < 1) return
    setIsLoading(true)
    const me = { id: user.id, name: `${user.firstName} ${user.lastName}` }
    const baSpyParticipants = participants.filter(user =>
      [ROLE_SPY, ROLE_DISTRIBUTOR].includes(user.role),
    )
    const { participantsNames } = baSpyParticipants.slice(0, 4).reduce(
      (acc, user, index) => {
        const roleToLabelMap = {
          [ROLE_SPY]: 'Spy',
          [ROLE_ADMIN]: 'Admin',
          [ROLE_DISTRIBUTOR]: 'BA',
          [ROLE_CITY_MANAGER]: 'CM',
        }
        acc.participantsNames += `${user.name}(${roleToLabelMap[user.role]})${
          index + 1 == baSpyParticipants.length ? '' : ', '
        }`
        return acc
      },
      {
        participantsNames: '',
      },
    )
    const { participantsIds } = participants.reduce(
      (acc, user) => {
        acc.participantsIds.push(user.id)
        return acc
      },
      { participantsIds: [] },
    )

    const cityId =
      user.role === ROLE_ADMIN ? participants[0].cityId : user.cityId

    const cityManagersResult = await getCityManagesByCityId(cityId)
    const cityManagers = cityManagersResult.data.cityManagers.edges.map(
      ({ node }) => node,
    )

    const cityResult = await getCityById(cityId)
    const countryId = cityResult.data.city.country.id
    const cityName = cityResult.data.city.name

    const adminsResult = await getAdminsByCountryId(countryId)
    const admins = adminsResult.data.admins.edges.map(({ node }) => node)

    const usersHashMap = convertArrayToHashMap(
      [...participants, ...cityManagers, ...admins],
      'id',
    )

    const allUserIds = Object.keys(usersHashMap)

    const talkJsUsers = await Promise.all(
      allUserIds.map(async id => {
        return { id, result: await getUserTalkJs(id) }
      }),
    )

    const usersNotFound = talkJsUsers.filter(
      item => item.result.errorCode === 'USER_NOT_FOUND',
    )

    await Promise.all(
      usersNotFound.map(async ({ id }) => {
        const user = usersHashMap[id]
        await createUpdateTalkJsUser(user)

        const cityResult = await getCityById(user.cityId)
        const countryId = cityResult.data.city.country.id

        await findConversationAndJoin({
          userId: user.id,
          role: user.role,
          cityId: user.cityId,
          countryId,
        })
      }),
    )

    const category =
      participantsIds.length === 1
        ? TALK_JS_CHAT_TYPES_MAP.SUPPORT
        : TALK_JS_CHAT_TYPES_MAP.GROUP

    let photoUrl
    if (participantsIds.length === 1) {
      if (participants[0].profilePicture) {
        photoUrl = participants[0].profilePicture
      } else {
        photoUrl =
          'https://oppizi-uploads.s3-ap-southeast-2.amazonaws.com/default-profile-pic.jpg?1688475921174'
      }
    }

    const data = {
      subject: `${participantsNames}`,
      conversationId: `${category}:${participantsIds.sort().join('')}`,
      participants: allUserIds,
      photoUrl: photoUrl ? photoUrl : null,
      custom: {
        category,
        cityId,
        countryId,
        cityName,
      },
    }

    try {
      const result = await createConversationTalkJs(data)
      if (result !== 'Already created') {
        await sendMessageTalkJs({
          conversationId: data.conversationId,
          body: [
            {
              text: 'Chat created',
              sender: me.id,
              type: 'UserMessage',
            },
          ],
        })
      } else {
        await joinToConversation({
          conversationId: data.conversationId,
          userId: user.id,
        })
      }
      setConversationId(data.conversationId)
      setParticipants([])
      setIsLoading(false)
    } catch (error) {
      console.error({ error })
    }
  }
  return (
    <ChatV2View
      viewType={viewType}
      cities={cities}
      citiesList={citiesList}
      setViewType={setViewType}
      onItemAdd={onItemAdd}
      participants={participants}
      searchRequestHandler={searchRequestHandler}
      onItemDelete={onItemDelete}
      handleCreateConversation={handleCreateConversation}
      conversationId={conversationId}
      user={user}
      isLoading={isLoading}
      userRole={user.role}
      chatOptions={chatOptions}
      getChatOptions={getChatOptions}
      getUnreadStatuses={getChatUnreadStatuses}
      unreadStatuses={unreadStatuses}
      setConversationId={setConversationId}
    />
  )
}

export default ChatV2
